// Australian tax withheld (PAYG withholding) for one pay, from the ATO's
// withholding schedules for payments made from 1 July 2026 (Taxation
// Administration (Withholding Schedules) Instrument 2026, F2026L00716):
//   Schedule 1  - the scales (1-6), tax offsets and Medicare levy adjustment
//   Schedule 8  - the study and training support loan (STSL) component
//   Schedule 15 - working holiday makers
// The answers come from the employee's TFN / withholding declaration (staff
// modal → Tax details). The coefficients change most 1 Julys - check them
// each financial year.

// The employee's answers - stored on the staff record (Staff.taxDeclaration).
export type TaxDeclaration = {
    tfnProvided: boolean;
    residency: 'resident' | 'foreign' | 'whm';
    claimsTaxFreeThreshold: boolean;
    studyLoan: boolean;
    medicareExemption: 'none' | 'half' | 'full';
    // Medicare levy reduction for a spouse / dependants on a low family income.
    medicareReduction: boolean;
    medicareReductionChildren: number;
    // Total tax offsets claimed on the withholding declaration, per year.
    taxOffsetsAnnual: number;
    // Working holiday makers: is the employer registered to withhold at their rates?
    whmRegisteredEmployer: boolean;
};

// The common case - what the form starts with, and assumes when nothing's saved.
export const DEFAULT_TAX_DECLARATION: TaxDeclaration = {
    tfnProvided: true,
    residency: 'resident',
    claimsTaxFreeThreshold: true,
    studyLoan: false,
    medicareExemption: 'none',
    medicareReduction: false,
    medicareReductionChildren: 0,
    taxOffsetsAnnual: 0,
    whmRegisteredEmployer: true,
};

type Row = [number, number, number]; // [weekly earnings less than, a, b]: y = a * x - b

// Schedule 1 coefficients.
const SCALES: Record<1 | 2 | 3 | 5 | 6, Row[]> = {
    // Tax-free threshold not claimed.
    1: [[188, 0.15, 0.15], [371, 0.2084, 11.0185], [515, 0.179, 0.1066], [932, 0.3227, 74.1674], [2246, 0.32, 71.6508], [3303, 0.39, 228.8816], [Infinity, 0.47, 493.1893]],
    // Tax-free threshold claimed.
    2: [[362, 0, 0], [538, 0.15, 54.3462], [673, 0.25, 108.2135], [721, 0.17, 54.3473], [865, 0.179, 60.8377], [1282, 0.3227, 185.1935], [2596, 0.32, 181.7319], [3653, 0.39, 363.4627], [Infinity, 0.47, 655.7704]],
    // Foreign residents.
    3: [[2596, 0.3, 0.3], [3653, 0.37, 181.7308], [Infinity, 0.45, 474.0385]],
    // Full exemption from the Medicare levy.
    5: [[362, 0, 0], [721, 0.15, 54.3462], [865, 0.159, 60.8365], [1282, 0.3027, 185.1923], [2596, 0.3, 181.7308], [3653, 0.37, 363.4615], [Infinity, 0.45, 655.7692]],
    // Half exemption from the Medicare levy.
    6: [[362, 0, 0], [721, 0.15, 54.3462], [865, 0.159, 60.8365], [908, 0.3027, 185.1923], [1135, 0.3527, 230.6135], [1282, 0.3127, 185.1923], [2596, 0.31, 181.7308], [3653, 0.38, 363.4615], [Infinity, 0.46, 655.7692]],
};

// Schedule 8 STSL component coefficients.
const STSL: Record<'claimed' | 'notClaimed', Row[]> = {
    // Tax-free threshold claimed, or a foreign resident.
    claimed: [[1337, 0, 0], [2494, 0.15, 200.5615], [3577, 0.17, 250.4527], [Infinity, 0.1, 0]],
    notClaimed: [[987, 0, 0], [2144, 0.15, 148.0615], [2727, 0.17, 190.9527], [Infinity, 0.1, 0]],
};

// Schedule 1 Medicare levy adjustment parameters.
const MEDICARE = {
    2: { threshold: 538, shadeIn: 673, levy: 0.02, shadeInRate: 0.1, shadeOutRate: 0.08 },
    6: { threshold: 908, shadeIn: 1135, levy: 0.01, shadeInRate: 0.05, shadeOutRate: 0.04 },
};
const FAMILY_THRESHOLD = 47238;
const PER_CHILD = 4338;

// Tax offsets reduce the withholding by this share of the annual amount per pay.
const OFFSET_SHARE: Record<string, number> = { Weekly: 0.019, Fortnightly: 0.038, Monthly: 0.083, Quarterly: 0.25 };

// Schedule 15: registered employer, under $45,000 paid in the year - the
// forecast can't know year-to-date payments, so the first bracket is assumed.
const WHM_RATE = 0.15;

// Nearest dollar, 50 cents rounding up.
const roundDollar = (n: number) => Math.floor(n + 0.5);
const fromTable = (rows: Row[], x: number) => {
    const [, a, b] = rows.find(([limit]) => x < limit)!;
    return Math.max(0, roundDollar(a * x - b));
};

type Cycle = 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Yearly';
export const PAYG_CYCLES: string[] = ['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Yearly'];

// Weekly earnings x for one pay (whole dollars plus 99 cents), per Schedule 1.
// Yearly isn't in the schedules - it goes through the weekly formula the same
// way (a close approximation for an annual payment).
const weeklyEarnings = (gross: number, cycle: Cycle) => {
    switch (cycle) {
        case 'Weekly': return Math.floor(gross) + 0.99;
        case 'Fortnightly': return Math.floor(gross / 2) + 0.99;
        case 'Monthly': {
            const cents = Math.round(gross * 100);
            const adjusted = (cents % 100 === 33 ? cents + 1 : cents) / 100;
            return Math.floor(adjusted * 3 / 13) + 0.99;
        }
        case 'Quarterly': return Math.floor(gross / 13) + 0.99;
        case 'Yearly': return Math.floor(gross / 52) + 0.99;
    }
};

// A rounded weekly amount, back to the pay cycle.
const fromWeekly = (weekly: number, cycle: Cycle) => {
    switch (cycle) {
        case 'Weekly': return weekly;
        case 'Fortnightly': return weekly * 2;
        case 'Monthly': return roundDollar(weekly * 13 / 3);
        case 'Quarterly': return weekly * 13;
        case 'Yearly': return weekly * 52;
    }
};

// Weekly Medicare levy adjustment (scales 2 and 6) for a family-based reduction.
const weeklyLevyAdjustment = (scale: 2 | 6, x: number, children: number) => {
    const p = MEDICARE[scale];
    const wft = Math.round(((FAMILY_THRESHOLD + children * PER_CHILD) / 52) * 100) / 100;
    const sop = Math.floor((wft * p.shadeInRate) / p.shadeOutRate);
    if (x < p.threshold || x >= sop) return 0;
    let wla: number;
    if (x < p.shadeIn) wla = (x - (scale === 2 ? 538.67 : 908.42)) * p.shadeInRate;
    else if (x < wft) wla = x * p.levy;
    else wla = wft * p.levy - (x - wft) * p.shadeOutRate;
    return Math.max(0, roundDollar(wla));
};

export type TaxWithheldBreakdown = {
    total: number;
    // Income tax part and study loan part - total is their sum.
    tax: number;
    studyLoan: number;
    // Which rule set applied, for the Tax details summary.
    basis: string;
};

// Tax withheld from one pay of `gross` at the given pay cycle, or null for a
// cycle there's no formula for (Ad-hoc).
export const calculateTaxWithheld = (gross: number, cycle: string, declaration: TaxDeclaration = DEFAULT_TAX_DECLARATION): TaxWithheldBreakdown | null => {
    if (!PAYG_CYCLES.includes(cycle)) return null;
    const c = cycle as Cycle;
    const d = { ...DEFAULT_TAX_DECLARATION, ...declaration };
    if (!(gross > 0)) return { total: 0, tax: 0, studyLoan: 0, basis: '' };
    const wholeDollars = Math.floor(gross);

    // Schedule 15 - working holiday makers (no offsets, Medicare adjustments or STSL).
    if (d.residency === 'whm') {
        if (!d.tfnProvided) {
            const tax = Math.floor(wholeDollars * 0.45);
            return { total: tax, tax, studyLoan: 0, basis: 'Working holiday maker, no TFN (45%)' };
        }
        if (d.whmRegisteredEmployer) {
            const tax = roundDollar(wholeDollars * WHM_RATE);
            return { total: tax, tax, studyLoan: 0, basis: 'Working holiday maker (15%, up to $45,000 a year)' };
        }
        // An unregistered employer withholds at foreign resident rates.
    }

    const foreign = d.residency !== 'resident';
    // Scale 4 - no TFN: a flat rate, cents ignored, and no study loan component.
    if (!d.tfnProvided) {
        const rate = foreign ? 0.45 : 0.47;
        const tax = Math.floor(wholeDollars * rate);
        return { total: tax, tax, studyLoan: 0, basis: `No TFN (${rate * 100}%)` };
    }

    const scale: 1 | 2 | 3 | 5 | 6 = foreign ? 3
        : !d.claimsTaxFreeThreshold ? 1
        : d.medicareExemption === 'full' ? 5
        : d.medicareExemption === 'half' ? 6
        : 2;
    const x = weeklyEarnings(gross, c);
    let tax = fromWeekly(fromTable(SCALES[scale], x), c);

    // Tax offsets - scales 2, 5 and 6 only.
    if ((scale === 2 || scale === 5 || scale === 6) && d.taxOffsetsAnnual > 0) {
        const perPay = c === 'Yearly' ? roundDollar(d.taxOffsetsAnnual * OFFSET_SHARE.Weekly) * 52 : roundDollar(d.taxOffsetsAnnual * OFFSET_SHARE[c]);
        tax = Math.max(0, tax - perPay);
    }
    // Medicare levy reduction for a low-income family - scales 2 and 6 only.
    const reduction = d.medicareReduction && (scale === 2 || scale === 6);
    if (reduction) {
        tax = Math.max(0, tax - fromWeekly(weeklyLevyAdjustment(scale, x, Math.max(0, d.medicareReductionChildren || 0)), c));
    }

    // STSL component - not with a family-based Medicare reduction.
    const studyLoan = d.studyLoan && !reduction
        ? fromWeekly(fromTable(d.claimsTaxFreeThreshold || foreign ? STSL.claimed : STSL.notClaimed, x), c)
        : 0;

    const basis = [
        foreign ? (d.residency === 'whm' ? 'Working holiday maker, unregistered employer (foreign resident rates)' : 'Foreign resident') : 'Resident',
        !foreign && (d.claimsTaxFreeThreshold ? 'tax-free threshold claimed' : 'no tax-free threshold'),
        scale === 5 && 'full Medicare exemption',
        scale === 6 && 'half Medicare exemption',
        reduction && 'Medicare levy reduction',
        studyLoan > 0 && 'study loan',
        d.taxOffsetsAnnual > 0 && (scale === 2 || scale === 5 || scale === 6) && 'tax offsets',
    ].filter(Boolean).join(', ');

    return { total: tax + studyLoan, tax, studyLoan, basis };
};

// Tax withheld only - kept for callers that just need the number.
export const calculatePaygWithholding = (gross: number, cycle: string, declaration?: TaxDeclaration): number | null =>
    calculateTaxWithheld(gross, cycle, declaration)?.total ?? null;
